import React, { useState } from 'react';
import { LeftOutlined, LockOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, message, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import './styles.less';
import { useHistory, useParams } from 'react-router';
import FullPageLoading from '@/components/FullPageLoading';
import { AppName, authBackgroundImg, VALIDATE_REQUIRED } from '@/config';
import { QuestionOutlined } from '@ant-design/icons';
import LanguageSelector from '@/components/app/LanguageSelector';
import { apiSdk } from '@/httpClient';
import { getUrlQueryValue } from '@/hooks/useQuery';

export const authLogo = require('@/assets/logos/applogo.png');

const ResetPassword = () => {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = React.useState<0 | 1 | 2>(0); //0:loading,1:invalid,2 valid
  // Lien d'invitation d'un employé (2026-11-02) : premier mot de passe, même page que « Mot de passe oublié ».
  const invitation = getUrlQueryValue('invitation') === '1';
  const titleKey = invitation ? 'resetPasswordPage_TitleInvitation' : 'resetPasswordPage_TitleResetPassword';
  const history = useHistory();
  const [passwordValidationMessages, setPasswordValidationMessages] = React.useState<string[]>([]);
  React.useEffect(() => {
    apiSdk.PasswordPolicyService.getValidationMessages().then(res => {
      setPasswordValidationMessages(res);
    });
  }, []);
  React.useEffect(() => {
    setTokenStatus(0);
    apiSdk.UserService.checkResetPasswordToken({
      token: getUrlQueryValue('token') || '',
    })
      .then(res => {
        if (res) {
          setTokenStatus(2);
        } else {
          setTokenStatus(1);
        }
      })
      .catch(() => {
        setTokenStatus(1);
      });
  }, []);
  const resetPassword = (password: string) => {
    setLoading(true);
    apiSdk.UserService.resetPassword({
      body: {
        token: getUrlQueryValue('token') || '',
        newPassword: password,
      },
    })
      .then(res => {
        if (res.success) {
          message.success(t('resetPasswordPage_ResetPasswordSuccess'));
          history.push('/cms/login');
        } else {
          message.error(t(res.message || ''));
        }
      })

      .finally(() => {
        setLoading(false);
      });
  };

  if (tokenStatus === 0) {
    return <FullPageLoading loading={true} />;
  }

  return (
    <div
      className="container-login-form"
      style={{ minHeight: '100vh', backgroundImage: `url(${authBackgroundImg})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }}
    >
      <Card className="login-form" bodyStyle={{ padding: 0, border: 'none' }} style={{ border: 'none' }}>
        <div
          style={{
            textAlign: 'center',
            marginBottom: 10,
            paddingTop: 20,
            paddingBottom: 20,
            position: 'relative',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#dddddd80',
          }}
        >
          <img src={authLogo} alt="auth-logo" style={{ width: 100 }} />
          <span style={{ color: '#0a8ca3', fontSize: 50, marginLeft: 10, textTransform: 'uppercase', fontWeight: 'bold' }}>{AppName}</span>

          <div className="change-language-container">
            <LanguageSelector />
          </div>
        </div>
        <div style={{ padding: 10, paddingBottom: 20 }}>
          {tokenStatus === 1 ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <div style={{ textAlign: 'center', fontSize: 20, color: '#f44336', marginBottom: 20 }}>
                {t('resetPasswordPage_ForgotPasswordSessionExpired')}
              </div>
              <Button
                icon={<LeftOutlined />}
                type="ghost"
                onClick={() => {
                  history.push('/cms/forgot-password');
                }}
              >
                {t('forgotPasswordPage_BackToLoginPage')}
              </Button>
            </div>
          ) : (
            <Form
              layout="vertical"
              name="normal_login"
              size="large"
              initialValues={{ remember: true }}
              onFinish={values => {
                resetPassword(values.password);
              }}
            >
              <h1 style={{ textAlign: 'center' }}>{t(titleKey)}</h1>
              <h4 style={{ textAlign: 'center', color: '#666', marginTop: 10 }}>
                {t(invitation ? 'resetPasswordPage_InvitationHint' : 'resetPasswordPage_TitleHint')}
              </h4>
              <Form.Item name="password" label={t('updatePassword_NewPassword')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input.Password
                  prefix={<LockOutlined />}
                  className="w-100"
                  addonAfter={
                    <Tooltip title={<div dangerouslySetInnerHTML={{ __html: passwordValidationMessages.join('<br/>') }} />}>
                      <QuestionOutlined />
                    </Tooltip>
                  }
                />
              </Form.Item>
              <Form.Item
                name="repassword"
                label={t('ConfirmPassword')}
                rules={[
                  { required: true, message: VALIDATE_REQUIRED },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(t('updatePassword_PasswordNotMatch'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  className="w-100"
                  prefix={<LockOutlined />}
                  addonAfter={
                    <Tooltip title={<div dangerouslySetInnerHTML={{ __html: passwordValidationMessages.join('<br/>') }} />}>
                      <QuestionOutlined />
                    </Tooltip>
                  }
                />
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}>
                <Button loading={loading} type="primary" htmlType="submit" className="login-form-button">
                  {t(titleKey)}
                </Button>
              </div>
              <Button
                icon={<LeftOutlined />}
                type="link"
                onClick={() => {
                  history.push('/cms/login');
                }}
              >
                {t('forgotPasswordPage_BackToLoginPage')}
              </Button>
            </Form>
          )}
        </div>
      </Card>
    </div>
  );
};
export default ResetPassword;
